import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Skeleton,
} from "@mui/material";

import DesignServicesIcon from "@mui/icons-material/DesignServices";   // Decorator icon
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"; // Charge icon
import TagIcon from "@mui/icons-material/Tag";                       // Display Number icon

import {
  getDecoratorsDetails,
  getSingleDecoratorDetails,
} from "../../../services/decorator";
import styles from "./index.module.css";
import CustomTextField from "../../CustomTextField";
import CustomDropdown from "../../CustomDropdownWithPlus";
import { resolveDecorationTablePrice, isValidDynamicWholeNumber } from "./tablePricing";
import { parseLegacyChargeBinding, getChargeSaveLabel, getSelectedListCharge, findUniqueMatrixChargeByName } from "./chargeLabels";

const normalizeNumericId = (value) =>
  String(value ?? "")
    .replace(/[^\d]/g, "")
    .trim();

export const resolveProductionInputHydration = ({
  dynamicFieldLabel,
  productionInputLabel,
  productionInputValue,
  selectedChargeId,
  persistedChargeId,
  userEdited,
} = {}) => {
  if (userEdited || !dynamicFieldLabel) return null;
  if (!isValidDynamicWholeNumber(productionInputValue)) return null;
  if (persistedChargeId && String(selectedChargeId || "") !== String(persistedChargeId)) return null;

  const savedLabel = String(productionInputLabel || "").trim();
  if (savedLabel && savedLabel !== String(dynamicFieldLabel).trim()) return null;

  return { field: dynamicFieldLabel, value: String(productionInputValue) };
};


// Matrix table input names depend on the selected charge (e.g. Stitches/Colors).
// Keep only shared form fields when opening another row or choosing a charge;
// otherwise a previous row's populated input blocks hydration of the current row.
export const resetPopupProductionInputs = (form = {}) => ({
  selectedDecorator: form.selectedDecorator ?? null,
  selectedCharge: form.selectedCharge ?? null,
  selectedCharges: null,
  productionDepartmentId: form.productionDepartmentId ?? null,
});

const shouldTriggerDialogSaveOnEnter = (event) => {
  if (event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return false;
  if (event.defaultPrevented || event.nativeEvent?.isComposing) return false;

  const target = event.target;
  if (!target) return true;

  const tagName = String(target.tagName || "").toUpperCase();
  if (tagName === "TEXTAREA") return false;

  if (target.isContentEditable) return false;
  if (target.getAttribute?.("aria-multiline") === "true") return false;
  if (target.closest?.('[role="textbox"][aria-multiline="true"]')) return false;

  return true;
};


const DecorationSelectorPopup = ({
  open,
  onClose,
  handleRowChange,
  selectedRowInfo,
  selectedRowData,
  onDecoratorPicked,
  syncBinding,
}) => {
  const [decoratorsOptions, setDecoratorsOptions] = useState([]);
  const [isLoadingDecorator, setIsLoadingDecorator] = useState(false);
  const [supplierinfo, setSupplierInfo] = useState({
    supplierId: null,
    addressId: null,
  });
  const [formData, setFormData] = useState({
    selectedDecorator: null,
    selectedCharge: null,     // chargeId (string/number)
    selectedCharges: [],      // for "Per Item"/"List" selection
    productionDepartmentId: null,
  });
  const [decoratorDetails, setDecoratorDetails] = useState(null);
  const [chargeOptions, setChargeOptions] = useState([]);
  const [loadedDecoratorId, setLoadedDecoratorId] = useState(null);
  const decoratorRequestRef = useRef(0);
  const productionInputEditedRef = useRef(false);

  // Explicit IDs are primary; this fallback recognizes older "<id>-<subname>" rows.
  const parseChargeBinding = parseLegacyChargeBinding;

  const toStrOrNull = (v) =>
    v === null || v === undefined || v === "" ? null : String(v);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === dynamicFieldLabel) {
      productionInputEditedRef.current = true;
      // Reject invalid typing/pastes, but never flag the retained valid value red.
      // Pricing validation below remains the authority for whether Save is safe.
      if (value !== "" && !isValidDynamicWholeNumber(value)) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Event-style change handler (works with CustomDropdown)
  const handleSelect = (eventOrValue, nameMaybe) => {
    let value;
    let name;

    if (eventOrValue && eventOrValue.target) {
      value = eventOrValue.target.value;
      name = eventOrValue.target.name || nameMaybe;
    } else {
      value = eventOrValue;
      name = nameMaybe;
    }

    if (!name) {
      console.warn("handleSelect called without a field name; ignoring", {
        eventOrValue,
        nameMaybe,
      });
      return;
    }

    if (name === "selectedCharge") {
      productionInputEditedRef.current = true;
      setFormData((prev) => ({
        ...resetPopupProductionInputs(prev),
        selectedCharge: value,
      }));
    } else if (name === "selectedDecorator") {
      productionInputEditedRef.current = true;
      setFormData((prev) => ({
        ...resetPopupProductionInputs(prev),
        selectedDecorator: value,
        selectedCharge: null,
        selectedCharges: [],
        productionDepartmentId: null,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Load one decorator (its charges)
  const fetchSingleDecorator = async (decoratorId, persistedChargeId, requestId) => {
    try {
      const response = await getSingleDecoratorDetails(
        decoratorId,
        null,
        persistedChargeId
      );
      // An older decorator lookup must not populate a newer row's popup.
      if (requestId !== decoratorRequestRef.current) return;

      const normalized = {
        ...response,
        Decorations: (response?.Decorations || []).map((d) => {
          // priceTableData normalization
          let ptd = d?.priceTableData;
          if (typeof ptd === "string") {
            try {
              ptd = JSON.parse(ptd);
            } catch {
              ptd = null;
            }
          }
          const headers = Array.isArray(ptd?.headers) ? ptd.headers : [];
          const rows = Array.isArray(ptd?.rows) ? ptd.rows : [];

          // ChargesList normalization
          let chargesList = [];
          if (Array.isArray(d?.ChargesList)) {
            chargesList = d.ChargesList;
          } else if (typeof d?.ChargesList === "string") {
            try {
              const parsed = JSON.parse(d.ChargesList);
              chargesList = Array.isArray(parsed) ? parsed : [];
            } catch {
              chargesList = [];
            }
          }

          return {
            ...d,
            id: normalizeNumericId(d?.id),
            priceTableData: { headers, rows },
            ChargesList: chargesList,
          };
        }),
      };

      setDecoratorDetails(normalized);

      const charges =
        normalized?.Decorations?.map((item) => ({
          name: item.archived
            ? `${item.ChargeName} (Archived / legacy)`
            : item.ChargeName,
          id: String(item.id),
          archived: Boolean(item.archived),
          disabled: Boolean(item.archived),
        })) || [];

      charges.sort((a, b) => {
        if (a.archived !== b.archived) return a.archived ? 1 : -1;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });

      setChargeOptions(charges);
      setLoadedDecoratorId(String(decoratorId));

      setSupplierInfo({
        supplierId: response?.supplierId || null,
        addressId: response?.addressId || null,
      });
    } catch (error) {
      if (requestId === decoratorRequestRef.current) {
        console.error("Unable to load decorator Matrix charges:", error);
        setChargeOptions([]);
        setDecoratorDetails(null);
      }
    } finally {
      if (requestId === decoratorRequestRef.current) setIsLoadingDecorator(false);
    }
  };

  // Load decorators list
  const fetchDecorators = async () => {
    try {
      const response = await getDecoratorsDetails();
      const options =
        response?.result?.map((decorator) => ({
          name: decorator.title,
          id: decorator.id.toString(),
          CurrencyCode: decorator.CurrencyCode ?? decorator.currencyCode ?? null,
          currencyCode: decorator.currencyCode ?? decorator.CurrencyCode ?? null,
          supplierId: decorator.supplierId ?? decorator.SupplierId ?? null,
          addressId: decorator.addressId ?? decorator.AddressId ?? null,
        })) || [];
      setDecoratorsOptions(options);
    } catch {
      // silent
    }
  };

  // Effects
  useEffect(() => {
    const requestId = ++decoratorRequestRef.current;
    setChargeOptions([]);
    setLoadedDecoratorId(null);
    setDecoratorDetails(null);
    setSupplierInfo({ supplierId: null, addressId: null });

    if (!open || !formData.selectedDecorator) {
      setIsLoadingDecorator(false);
      return () => { decoratorRequestRef.current += 1; };
    }

    setIsLoadingDecorator(true);
    const originalDecorator = String(formData.selectedDecorator) ===
      String(selectedRowData?.decoratorId ?? "");
    const persistedChargeId = originalDecorator
      ? (toStrOrNull(selectedRowData?.chargeId) ||
        parseChargeBinding(selectedRowData?.chargeName).chargeId)
      : null;
    fetchSingleDecorator(formData.selectedDecorator, persistedChargeId, requestId);
    return () => { decoratorRequestRef.current += 1; };
  }, [open, formData.selectedDecorator, selectedRowInfo?.decorationIndex, selectedRowInfo?.rowIndex]);

  useEffect(() => {
    fetchDecorators();
  }, []);

  // Seed from existing row when dialog opens
  useEffect(() => {
    if (!open) return;

    const incomingDecId = toStrOrNull(selectedRowData?.decoratorId);
    const incomingChargeId = toStrOrNull(selectedRowData?.chargeId);
    const { chargeId: parsedChargeId } = parseChargeBinding(
      selectedRowData?.chargeName
    );
    const incomingProdDeptId = selectedRowData?.productionDepartmentId ?? null;

    setFormData((f) => ({
      ...f,
      selectedDecorator: incomingDecId || null,
      selectedCharge: incomingChargeId || parsedChargeId || null,
      productionDepartmentId: incomingProdDeptId,
    }));
  }, [open, selectedRowData]);

  useEffect(() => {
    if (!open) return;
    productionInputEditedRef.current = false;
    // Each decoration charge has its own Matrix production input, even when
    // two locations use the same charge ID and dynamic table header.
    setFormData(resetPopupProductionInputs);
  }, [open, selectedRowInfo?.decorationIndex, selectedRowInfo?.rowIndex]);

  // After charge options load, if we still don't have a selectedCharge, backfill from binding
  useEffect(() => {
    if (!open) return;
    if (!chargeOptions?.length) return;
    if (String(loadedDecoratorId) !== String(formData.selectedDecorator)) return;
    if (String(formData.selectedDecorator) !== String(selectedRowData?.decoratorId ?? "")) return;
    if (formData.selectedCharge) return;

    const byId = toStrOrNull(selectedRowData?.chargeId);
    const { chargeId: parsed } = parseChargeBinding(
      selectedRowData?.chargeName
    );
    const candidate = byId || parsed;

    if (candidate && chargeOptions.some((c) => String(c.id) === String(candidate))) {
      setFormData((prev) => ({
        ...prev,
        selectedCharge: candidate,
      }));
    }
  }, [open, chargeOptions, selectedRowData, formData.selectedCharge, loadedDecoratorId, formData.selectedDecorator]);

  // Existing row metadata must never leak into a newly selected decorator.
  const isOriginalDecorator = String(formData.selectedDecorator ?? "") ===
    String(selectedRowData?.decoratorId ?? "");

  // Legacy rows can retain their label and pricing after losing the Matrix ID.
  // Resolve only an unambiguous, active option belonging to this decorator.
  const recoveredCharge = useMemo(() => {
    if (!open || !isOriginalDecorator || formData.selectedCharge || selectedRowData?.chargeId != null) return null;
    if (String(loadedDecoratorId) !== String(formData.selectedDecorator)) return null;
    if (parseChargeBinding(selectedRowData?.chargeName).chargeId) return null;
    return findUniqueMatrixChargeByName({
      chargeName: selectedRowData?.chargeName,
      chargeOptions,
    });
  }, [open, isOriginalDecorator, formData.selectedCharge, selectedRowData, chargeOptions, loadedDecoratorId, formData.selectedDecorator]);

  const effectiveChargeId = useMemo(() => (
    formData.selectedCharge ??
    (isOriginalDecorator ? toStrOrNull(selectedRowData?.chargeId) : null) ??
    (isOriginalDecorator ? parseChargeBinding(selectedRowData?.chargeName).chargeId : null) ??
    recoveredCharge?.id ??
    null
  ), [formData.selectedCharge, selectedRowData, recoveredCharge, isOriginalDecorator]);

  // Charge detail by normalized id
  const selectedChargeDetail = useMemo(() => {
    const want = normalizeNumericId(effectiveChargeId);
    return (decoratorDetails?.Decorations || []).find(
      (it) => normalizeNumericId(it?.id) === want
    );
  }, [decoratorDetails, effectiveChargeId]);

  // table/dynamic field visibility
  const showDynamicField =
    (selectedChargeDetail?.ChargeType === "Run" ||
      selectedChargeDetail?.ChargeType === "Fixed") &&
    (selectedChargeDetail?.Type === "Table" ||
      selectedChargeDetail?.CalculationType === "Table");

  const filteredHeaders = selectedChargeDetail?.priceTableData?.headers;
  const dynamicFieldLabel = filteredHeaders?.[0];

  // The canonical value is stored independently of the dynamic table header.
  // Restore it only after charge details reveal that header, and only while the
  // user has not changed this popup's production input or charge selection.
  useEffect(() => {
    if (!open) return;
    const persistedChargeId =
      toStrOrNull(selectedRowData?.chargeId) ||
      parseChargeBinding(selectedRowData?.chargeName).chargeId;
    const hydration = resolveProductionInputHydration({
      dynamicFieldLabel,
      productionInputLabel: selectedRowData?.productionInputLabel,
      productionInputValue: selectedRowData?.productionInputValue,
      selectedChargeId: effectiveChargeId,
      persistedChargeId,
      userEdited: productionInputEditedRef.current,
    });
    if (!hydration) return;
    setFormData((prev) => {
      if (prev[hydration.field] !== undefined && prev[hydration.field] !== "") return prev;
      return { ...prev, [hydration.field]: hydration.value };
    });
  }, [open, dynamicFieldLabel, effectiveChargeId, selectedRowData]);

  // Prefer backend-provided routing metadata when available; backend save remains authoritative.
  useEffect(() => {
    if (!selectedChargeDetail) return;
    setFormData((prev) => ({
      ...prev,
      productionDepartmentId: selectedChargeDetail?.productionDepartmentId ?? null,
    }));
  }, [selectedChargeDetail]);

  // UI helpers
  const getChargeNameById = (id) => {
    if (!id) return null;
    return chargeOptions.find((c) => String(c.id) === String(id))?.name || null;
  };

  // Resolve pricing synchronously with the field and quantity. A single
  // explicit "Each Additional..." Matrix row can extend the last stitch tier.
  // Unlike the old effect, this cannot leave stale/blank row state on save.
  const tablePricing = useMemo(() => {
    if (!showDynamicField || !selectedChargeDetail) return null;
    return resolveDecorationTablePrice({
      rows: selectedChargeDetail.priceTableData?.rows,
      inputHeader: dynamicFieldLabel,
      inputValue: formData?.[dynamicFieldLabel],
      quantity: selectedRowData?.quantity || 1,
      chargeType: selectedChargeDetail.ChargeType,
    });
  }, [showDynamicField, selectedChargeDetail, dynamicFieldLabel, formData?.[dynamicFieldLabel], selectedRowData?.quantity]);
  const filteredRows = tablePricing?.row || null;

  // Valid list charges (Per Item/List)
  const validCharges = useMemo(() => {
    return (selectedChargeDetail?.ChargesList || []).filter(
      (charge) =>
        String(charge?.ChargeName || "").trim() &&
        charge?.Price != null &&
        String(charge.Price).trim() !== "" &&
        Number.isFinite(Number(charge.Price))
    );
  }, [selectedChargeDetail]);

  const handleCheckboxChange = (charge) => {
    setFormData((prev) => ({ ...prev, selectedCharges: charge }));
  };

  // List selections must refer to the active charge, and a saved legacy sub-item
  // should survive reopening rather than silently reverting to the first item.
  const chargeCalcType = selectedChargeDetail?.CalculationType || selectedChargeDetail?.Type || "";
  const isListCharge = chargeCalcType === "Per Item" ||
    chargeCalcType === "List" || selectedChargeDetail?.ChargeType === "Per Item";
  const selectedListCharge = isListCharge ? getSelectedListCharge({
    charges: validCharges,
    selected: formData.selectedCharges,
    savedChargeName: isOriginalDecorator ? selectedRowData?.chargeName : null,
    chargeId: effectiveChargeId,
    savedChargeId: isOriginalDecorator
      ? (selectedRowData?.chargeId ??
        parseChargeBinding(selectedRowData?.chargeName).chargeId ??
        recoveredCharge?.id)
      : null,
    parentChargeName: selectedChargeDetail?.ChargeName,
  }) : null;
  const readableChargeName = selectedChargeDetail
    ? getChargeSaveLabel({
      chargeName: selectedChargeDetail.ChargeName,
      chargeId: effectiveChargeId,
      listItem: selectedListCharge,
    })
    : "";
  const hasListPrice = !isListCharge ||
    (selectedListCharge != null && Number.isFinite(Number(selectedListCharge.Price)));

  // ======== NEW: Save gating (decorator + charge + dynamic when required) ========
  const isFilled = (v) => v != null && String(v).trim() !== "";
  const requiresDynamic = Boolean(showDynamicField && dynamicFieldLabel);
  const hasDecorator = isFilled(formData?.selectedDecorator);
  const hasCharge = isFilled(effectiveChargeId) && Boolean(selectedChargeDetail) &&
    String(loadedDecoratorId) === String(formData.selectedDecorator);
  const hasDynamic = !requiresDynamic ||
    isValidDynamicWholeNumber(formData?.[dynamicFieldLabel]);
  const hasTablePricing = !showDynamicField || tablePricing?.netCost != null;
  const disableSave = !(hasDecorator && hasCharge && hasDynamic && hasTablePricing && hasListPrice);
  // Only an advisory: exceptionally large numbers remain valid if the Matrix
  // explicitly prices them. This must not mark the field red or disable Save.
  const warnLargeProductionInput = requiresDynamic &&
    isValidDynamicWholeNumber(formData?.[dynamicFieldLabel]) &&
    Number(formData[dynamicFieldLabel]) > 100000;
  // ==============================================================================

  // Save → write back to parent row
  const handleSave = () => {
    if (disableSave) {
      if (showDynamicField && !hasTablePricing && hasDynamic) {
        alert(tablePricing?.error || "No Matrix pricing is available for this selection.");
        return;
      }
      const reasons = [];
      if (!hasDecorator) reasons.push("Decorator");
      if (!hasCharge) reasons.push("Charge");
      if (!hasDynamic) reasons.push(dynamicFieldLabel || "Required table value");
      if (!hasTablePricing) reasons.push("Valid Matrix pricing");
      if (!hasListPrice) reasons.push("Configured list charge price");
      alert(`Please select: ${reasons.join(", ")}.`);
      return;
    }

    try {
      const { decorationIndex, rowIndex } = selectedRowInfo || {};
      const toNumLocal = (v) => (v == null || v === "" ? null : Number(v));

      const decId = toNumLocal(formData.selectedDecorator);

      // The dropdown and Save must use the same effective ID, including
      // a unique legacy name match that has not yet been persisted.
      const chargeId = toNumLocal(effectiveChargeId);

      const previousDecoratorId = toNumLocal(selectedRowData?.decoratorId);
      if (decId && decId !== previousDecoratorId && typeof onDecoratorPicked === "function") {
        const decRow =
          (decoratorsOptions || []).find(
            (d) => String(d.id) === String(decId)
          ) || null;
        const decName = decRow?.name || decRow?.title || "";
        onDecoratorPicked(decorationIndex, {
          id: decId,
          title: decName,
          rowIndexScope: rowIndex,
          CurrencyCode: decRow?.CurrencyCode ?? decRow?.currencyCode ?? null,
          currencyCode: decRow?.currencyCode ?? decRow?.CurrencyCode ?? null,
          supplierId: decRow?.supplierId ?? null,
          addressId: decRow?.addressId ?? null,
          decorator: decRow,
        });
      }

      const detail = selectedChargeDetail;
      const hasValidCharge = Boolean(chargeId && detail);
      const sameChargeId = Number(selectedRowData?.chargeId) === chargeId;
      const repairedLegacyBinding = selectedRowData?.chargeId == null &&
        recoveredCharge && String(recoveredCharge.id) === String(chargeId);
      // Restoring an ID is not a price change. An explicit change to the
      // Matrix input or list sub-charge IS a price change, even with the same ID.
      const savedInput = selectedRowData?.productionInputValue;
      const currentInput = requiresDynamic ? formData[dynamicFieldLabel] : null;
      const inputChanged = requiresDynamic &&
        (savedInput == null || Number(savedInput) !== Number(currentInput));
      const savedName = String(selectedRowData?.chargeName ?? "").trim();
      const listSelectionChanged = isListCharge && savedName &&
        savedName !== String(readableChargeName ?? "").trim();
      const preserveExistingPricing = Boolean(
        (repairedLegacyBinding || sameChargeId) &&
        !inputChanged &&
        !listSelectionChanged
      );

      const chargeType = detail?.ChargeType || "";
      // Persist the Matrix charge classification on the editable row.
      // The decoration copy flow must not need to fetch the Matrix again.
      if (chargeType === "Run" || chargeType === "Fixed") {
        handleRowChange(
          decorationIndex,
          rowIndex,
          "subType",
          chargeType === "Run" ? "Run Charge" : "Fixed Charge"
        );
      }
      const calcType = chargeCalcType;

      const productionDepartmentId =
        formData.productionDepartmentId ?? detail?.productionDepartmentId ?? null;
      const productionInputLabel = dynamicFieldLabel || null;
      const productionInputValue =
        requiresDynamic && hasDynamic
          ? Number(formData[dynamicFieldLabel])
          : null;

      // ── A) Run/Table → quantity-based from row ranges
      if (
        hasValidCharge &&
        chargeType === "Run" &&
        calcType === "Table" &&
        filteredRows
      ) {
        const netCost = tablePricing.netCost;

        handleRowChange(
          decorationIndex,
          rowIndex,
          "chargeName",
          readableChargeName
        );
        if (!preserveExistingPricing) handleRowChange(decorationIndex, rowIndex, "netCost", netCost);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "supplierId",
          supplierinfo?.supplierId
        );
        handleRowChange(
          decorationIndex,
          rowIndex,
          "addressId",
          supplierinfo?.addressId
        );
        handleRowChange(decorationIndex, rowIndex, "decoratorId", decId);
        handleRowChange(decorationIndex, rowIndex, "chargeId", chargeId);
        handleRowChange(decorationIndex, rowIndex, "productionInputLabel", productionInputLabel);
        handleRowChange(decorationIndex, rowIndex, "productionInputValue", productionInputValue);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "productionDepartmentId",
          productionDepartmentId
        );
        if (syncBinding) syncBinding(decorationIndex, rowIndex);
      }

      // ── B) Fixed/Table → use Cost column
      else if (
        hasValidCharge &&
        chargeType === "Fixed" &&
        calcType === "Table" &&
        filteredRows
      ) {
        const netCost = tablePricing.netCost;

        handleRowChange(
          decorationIndex,
          rowIndex,
          "chargeName",
          readableChargeName
        );
        if (!preserveExistingPricing) handleRowChange(decorationIndex, rowIndex, "netCost", netCost);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "supplierId",
          supplierinfo?.supplierId
        );
        handleRowChange(
          decorationIndex,
          rowIndex,
          "addressId",
          supplierinfo?.addressId
        );
        handleRowChange(decorationIndex, rowIndex, "decoratorId", decId);
        handleRowChange(decorationIndex, rowIndex, "chargeId", chargeId);
        handleRowChange(decorationIndex, rowIndex, "productionInputLabel", productionInputLabel);
        handleRowChange(decorationIndex, rowIndex, "productionInputValue", productionInputValue);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "productionDepartmentId",
          productionDepartmentId
        );
        if (syncBinding) syncBinding(decorationIndex, rowIndex);
      }

      // ── C) Per Item / List → use ChargesList (selected or fallback first)
      else if (
        hasValidCharge &&
        isListCharge
      ) {
        const price = Number(selectedListCharge.Price);

        handleRowChange(decorationIndex, rowIndex, "chargeName", readableChargeName);
        if (!preserveExistingPricing) handleRowChange(decorationIndex, rowIndex, "netCost", price);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "supplierId",
          supplierinfo?.supplierId
        );
        handleRowChange(
          decorationIndex,
          rowIndex,
          "addressId",
          supplierinfo?.addressId
        );
        handleRowChange(decorationIndex, rowIndex, "decoratorId", decId);
        handleRowChange(decorationIndex, rowIndex, "chargeId", chargeId);
        handleRowChange(decorationIndex, rowIndex, "productionInputLabel", productionInputLabel);
        handleRowChange(decorationIndex, rowIndex, "productionInputValue", productionInputValue);
        handleRowChange(
          decorationIndex,
          rowIndex,
          "productionDepartmentId",
          productionDepartmentId
        );
        if (syncBinding) syncBinding(decorationIndex, rowIndex);
      }

      // ── D) No match → at least persist decorator change (if changed)
      else {
        const prevRowDecoratorId = Number(selectedRowData?.decoratorId ?? 0);
        const decoratorChanged = !!decId && decId !== prevRowDecoratorId;

        if (decoratorChanged) {
          handleRowChange(decorationIndex, rowIndex, "decoratorId", decId);
          handleRowChange(decorationIndex, rowIndex, "chargeId", null);
          handleRowChange(decorationIndex, rowIndex, "chargeName", "");
          handleRowChange(decorationIndex, rowIndex, "netCost", 0);
          handleRowChange(decorationIndex, rowIndex, "productionInputLabel", null);
          handleRowChange(decorationIndex, rowIndex, "productionInputValue", null);
          handleRowChange(
            decorationIndex,
            rowIndex,
            "productionDepartmentId",
            null
          );
          if (syncBinding) syncBinding(decorationIndex, rowIndex);
        }
      }
    } catch (err) {
      console.error("handleSave ERROR:", err);
      alert(`handleSave ERROR: ${String(err?.message || err)}`);
      return;
    }

    onClose();
    setFormData((prev) => ({
      ...prev,
      selectedDecorator: null,
      selectedCharge: null,
      selectedCharges: null,
    }));
  };

  // Summary helpers
  const saved = parseChargeBinding(selectedRowData?.chargeName);
  const baseName =
    getChargeNameById(effectiveChargeId) ||
    (isOriginalDecorator && !saved.subName ? selectedRowData?.chargeName : "") ||
    (effectiveChargeId ? `#${effectiveChargeId}` : "—");

  const chargeSummary = readableChargeName || baseName || saved.subName || "—";

  const displayHeader = dynamicFieldLabel || "Display Number";

  const handleDialogKeyDown = (event) => {
    if (!shouldTriggerDialogSaveOnEnter(event)) return;
    if (disableSave) return;

    event.preventDefault();
    handleSave();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={handleDialogKeyDown}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "1250px",
        },
      }}
    >
      <DialogTitle className="create-order-dialog-title">Add Charge</DialogTitle>
      <DialogContent className="create-order-dialog-content">
  {
     //JSON.stringify(formData)
  }

  {/* ────────────────────────────────────────────────
      Top 3-column header: Decorator / Charge / Display Number
      ──────────────────────────────────────────────── */}
  <Box className={styles.headerRow}>
    {/* 1) Decorator column */}
    <Box className={styles.headerCol}>
      <Box className={styles.headerLabel}>
        <DesignServicesIcon className={styles.headerLabelIcon} />
        <span>Decorator</span>
      </Box>

      <Box className={styles.headerFieldShell}>
        <CustomDropdown
          label=""
          name="selectedDecorator"
          options={decoratorsOptions}
          handleSelect={(v) => handleSelect(v, "selectedDecorator")}
          selectedValue={formData?.selectedDecorator ?? ""}
        />
      </Box>
    </Box>

    {/* 2) Charge column */}
    <Box className={styles.headerCol}>
      <Box className={styles.headerLabel}>
        <MonetizationOnIcon className={styles.headerLabelIcon} />
        <span>Charge</span>
      </Box>

      <Box className={styles.headerFieldShell}>
        {isLoadingDecorator ? (
          <Skeleton
            variant="rectangular"
            height={40}
            width="100%"
            sx={{ borderRadius: 1 }}
          />
        ) : formData.selectedDecorator ? (
          <CustomDropdown
            label=""
            name="selectedCharge"
            options={chargeOptions}
            handleSelect={(v) => handleSelect(v, "selectedCharge")}
            selectedValue={effectiveChargeId ?? ""}
          />
        ) : (
          <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
            Select a decorator first
          </Typography>
        )}
      </Box>
    </Box>

    {/* 3) Display Number / dynamic field column */}
{/* 3) Display Number / dynamic field column */}
<Box className={styles.headerCol}>
  <Box className={styles.headerLabel}>
    <TagIcon className={styles.headerLabelIcon} />
    <span>{dynamicFieldLabel || "Display Number"}</span>
  </Box>

  <Box className={styles.headerFieldShell}>
    {!isLoadingDecorator && showDynamicField && dynamicFieldLabel ? (
      <CustomTextField
        label=""
        name={dynamicFieldLabel}
        placeholder={`Enter ${dynamicFieldLabel.toLowerCase()}`}
        value={formData?.[dynamicFieldLabel] ?? ""}
        handleChange={handleInputChange}
        type="text"
        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
      />
    ) : (
      <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
        {showDynamicField
          ? "Enter a value to pull table pricing"
          : "No display value required for this charge"}
      </Typography>
    )}
  </Box>
  {!isLoadingDecorator && requiresDynamic &&
    (warnLargeProductionInput || !isFilled(formData?.[dynamicFieldLabel])) && (
    <Typography variant="caption" sx={{
      display: "block",
      mt: 0.75,
      px: 0.5,
      color: warnLargeProductionInput ? "warning.dark" : "text.secondary",
    }}>
      {warnLargeProductionInput
        ? "Unusually high value — please verify before saving."
        : "Whole numbers only (0 or greater)."}
    </Typography>
  )}
</Box>

  </Box>

  {/* ────────────────────────────────────────────────
      Current selection summary
      ──────────────────────────────────────────────── */}
  {(formData.selectedDecorator || effectiveChargeId) && (
    <Box
      mt={1}
      mb={1}
      p={1.5}
      sx={{
        border: "1px dashed #ccc",
        borderRadius: 1,
        backgroundColor: "#fafafa",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Current selection
      </Typography>
      <Typography variant="body2">
        <b>Decorator:</b>{" "}
        {decoratorsOptions.find(
          (d) => String(d.id) === String(formData.selectedDecorator)
        )?.name ||
          (selectedRowData?.decoratorId
            ? `#${selectedRowData.decoratorId}`
            : "—")}
      </Typography>
      <Typography variant="body2">
        <b>Charge:</b> {chargeSummary}
      </Typography>

      {selectedChargeDetail?.ChargeType === "Run" &&
        (selectedChargeDetail?.Type === "Table" ||
          selectedChargeDetail?.CalculationType === "Table") &&
        filteredRows && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>Net cost preview:</b>{" "}
            {(() => {
              return tablePricing.netCost.toFixed(2);
            })()}
          </Typography>
        )}
    </Box>
  )}

  {/* ────────────────────────────────────────────────
      Matched pricing row (Table)
      ──────────────────────────────────────────────── */}
  {showDynamicField && isFilled(formData?.[dynamicFieldLabel]) && tablePricing?.error && (
    <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
      {tablePricing.error} The current charge has not been changed.
    </Typography>
  )}
  {showDynamicField && filteredRows && (
    <Box mt={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Matched Pricing Row
      </Typography>
      {tablePricing?.surchargeSteps > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Base at {Number(tablePricing.threshold).toLocaleString()} + {tablePricing.surchargeSteps} additional
          {tablePricing.surchargeSteps === 1 ? " increment" : " increments"} of {Number(tablePricing.increment).toLocaleString()}.
          Calculated NET per piece: ${tablePricing.netCost.toFixed(2)}.
        </Typography>
      )}
      <table className={styles.priceTable}>
        <thead>
          <tr>
            {filteredHeaders.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(tablePricing?.surchargeRow
            ? [filteredRows, tablePricing.surchargeRow]
            : [filteredRows]).map((pricingRow, pricingIndex) => (
            <tr key={pricingIndex}>
              {filteredHeaders.map((header, index) => (
                <td key={index}>{pricingRow[header]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  )}

  {/* ────────────────────────────────────────────────
      Per Item / List charges selector
      ──────────────────────────────────────────────── */}
  {(selectedChargeDetail?.ChargeType === "Per Item" ||
    selectedChargeDetail?.Type === "List" ||
    selectedChargeDetail?.CalculationType === "List") &&
    (validCharges || []).length > 0 && (
      <Box mt={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Available Charges (for #{String(effectiveChargeId || "")})
        </Typography>
        <Box mt={0.5}>
          {validCharges.map((charge, index) => (
            <Box key={index} display="flex" alignItems="center" mb={0.5}>
              <input
                type="checkbox"
                checked={
                  selectedListCharge === charge
                }
                onChange={() => handleCheckboxChange(charge)}
                style={{ marginRight: 8 }}
              />
              <Typography>
                {charge.ChargeName} - ${charge.Price}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    )}

  {/* ────────────────────────────────────────────────
      Decorator note
      ──────────────────────────────────────────────── */}
  {selectedChargeDetail ? (
    <Box mt={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Decorator Note
      </Typography>
      <Typography variant="body2" mb={2}>
        {selectedChargeDetail.Description || "No description provided."}
      </Typography>
    </Box>
  ) : null}
</DialogContent>


      <Box display="flex" justifyContent="flex-end" gap={1} p={2}>
        <Button variant="contained" onClick={handleSave} disabled={disableSave}>
          Save
        </Button>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: "white !important",
            color: "red !important",
            border: "2px solid gray !important",
          }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};

export default memo(DecorationSelectorPopup);
